<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import useHighlightsStore, { type HighlightWithText } from '../../stores/highlights';
import useUiStore from '../../stores/ui';
import useRenditionStore from '../../stores/rendition';
import HighlightListItem from './HighlightListItem.vue';
import HighlightFormDialog from './HighlightFormDialog.vue';
import useLocale from '../../composables/useLocale';
import type { GoToItem } from '../../types';
import type { Annotation } from '@ilteducation/annotations-types';

type Emits = {
  (e: 'goToAnnotation', item: GoToItem): void;
};
const emit = defineEmits<Emits>();

type Props = {
  search: string;
  bookId: string;
  profileId?: string;
};
const props = defineProps<Props>();

const { t } = useLocale();
const { toggleAnnotationsDrawer } = useUiStore();
const highlightsStore = useHighlightsStore();
const { highlights } = storeToRefs(highlightsStore);
const { removeHighlight, updateHighlight } = highlightsStore;
const { compareCFIs } = useRenditionStore();

const showEditDialog = ref(false);
const editingHighlight = ref<HighlightWithText | null>(null);
const isSaving = ref(false);
const isRemoving = ref(false);
const workingId = ref<string | null>(null); // Track which highlight is being saved/deleted

const sortHighlightsByCFI = (highlightArray: HighlightWithText[]): HighlightWithText[] =>
  highlightArray.sort((a, b) => compareCFIs(a.range, b.range));

const filteredHighlights = computed(() => {
  const query = props.search.toLowerCase().trim();
  const filtered = query
    ? highlights.value.filter((highlight) => highlight.text.toLowerCase().includes(query))
    : highlights.value;

  return sortHighlightsByCFI([...filtered]);
});

const handleGoToAnnotation = (item: GoToItem) => {
  // Close annotations drawer when navigating
  setTimeout(() => toggleAnnotationsDrawer(false), 100);

  emit('goToAnnotation', item);
};

const handleEdit = (highlight: HighlightWithText) => {
  editingHighlight.value = highlight;
  showEditDialog.value = true;

  setTimeout(() => toggleAnnotationsDrawer(false), 100);
};

const handleSave = async (color: string, type: Annotation['type'], comment?: string) => {
  if (!editingHighlight.value) return;

  isSaving.value = true;
  try {
    const updated: HighlightWithText = {
      ...editingHighlight.value,
      color,
      type: type as any,
      comment: comment?.trim() ? { type: 'text', content: comment } : undefined,
    };
    await updateHighlight(updated);
    showEditDialog.value = false;
  } finally {
    isSaving.value = false;
  }
};

const handleRemove = async (highlight: HighlightWithText) => {
  workingId.value = highlight.id;
  isRemoving.value = true;
  try {
    await removeHighlight(highlight);
    showEditDialog.value = false;
  } finally {
    isRemoving.value = false;
    workingId.value = null;
  }
};
</script>

<template>
  <v-sheet data-testid="bp-highlights-tab">
    <v-list v-if="filteredHighlights.length">
      <HighlightListItem
        v-for="highlight in filteredHighlights"
        :key="highlight.id"
        :highlight="highlight"
        :is-saving="workingId === highlight.id"
        @go-to-annotation="handleGoToAnnotation"
        @edit="handleEdit"
        @remove="handleRemove(highlight)"
      />
    </v-list>

    <p v-else class="text-center pa-4">
      {{ t('book_player.message_no_highlights_found') }}
    </p>

    <HighlightFormDialog
      v-if="editingHighlight"
      v-model="showEditDialog"
      :highlight="editingHighlight"
      :is-saving="isSaving"
      :is-removing="isRemoving"
      @save="handleSave"
      @remove="handleRemove(editingHighlight)"
    />
  </v-sheet>
</template>
